import type { ScmConnection } from '@/api/schemas';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ConnectForm } from './ConnectScmForm';
import { ReplaceForm } from './ReplaceScmTokenForm';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Connect Bitbucket: a repository access token (one repository, any plan) or a workspace access token (Premium), with the
 * in-sheet guide to creating it (plan P34), the workspace (and repository) slug and a name. The token goes once to
 * `POST /credentials` (`bitbucket_access_token`, stored encrypted, never returned) and the connection references the
 * credential id (`POST /scm-connections`): the console never keeps or shows the token.
 */
export function ScmConnectionSheet({ open, onOpenChange }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="scm-connection-sheet" className="w-full gap-0 p-0" aria-describedby="scm-connection-desc">
        {open ? <ConnectForm onDone={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

/** Replace a connection's token in place (`POST /credentials/{id}/rotate`: same id, so the connection keeps working). */
export function ReplaceScmTokenDialog({ connection, onOpenChange }: { connection: ScmConnection | null; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={Boolean(connection)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="replace-scm-token-dialog" aria-describedby="replace-scm-token-desc">
        {connection ? <ReplaceForm key={connection.id} connection={connection} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
